import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { WifiOff, RefreshCw } from 'lucide-react';
import { QueuedSalesDialog } from '@/components/QueuedSales';

// Subtle, non-blocking status bar that reassures staff their sales are safe when
// the network drops, and that queued sales are syncing once it returns. Tapping
// it opens the queued-sales list. Hidden when everything is synced.
export function OfflineBanner() {
  const { user, online, syncing, pendingCount } = useApp();
  const [open, setOpen] = useState(false);

  if (!user) return null;
  const hasQueue = pendingCount > 0;
  if (online && !hasQueue && !syncing) return null;

  const offline = !online;
  const label = offline
    ? (hasQueue
        ? `Offline — ${pendingCount} sale${pendingCount === 1 ? '' : 's'} saved, will sync`
        : 'Offline — sales saved, will sync')
    : `Syncing ${pendingCount || ''} queued sale${pendingCount === 1 ? '' : 's'}…`.replace('  ', ' ');

  return (
    <>
      <div
        className="pointer-events-none fixed inset-x-0 z-[250] flex justify-center px-4"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)' }}
        role="status"
        aria-live="polite"
        data-testid="offline-banner"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`pointer-events-auto flex max-w-[92vw] items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium shadow-lg backdrop-blur transition-transform active:scale-[0.98] ${
            offline
              ? 'border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300'
              : 'border-primary/30 bg-primary/10 text-primary'
          }`}
          data-testid={offline ? 'offline-banner-offline' : 'offline-banner-syncing'}
        >
          {offline
            ? <WifiOff className="h-3.5 w-3.5 shrink-0" />
            : <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" />}
          <span className="truncate">{label}</span>
          <span className="ml-0.5 shrink-0 rounded-full bg-black/5 px-1.5 text-[10px] font-bold opacity-90 dark:bg-white/15">View</span>
        </button>
      </div>
      <QueuedSalesDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
